<template>
    <div :id="modalID" class="modal fade" data-bs-backdrop="static" data-bs-keyboard="true" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-scrollable modal-dialog-centered" role="document" style="min-width: 550px">
            <div class="modal-content">
                <div class="modal-header d-flex align-items-center">
                    <div style="font-size: 15px; font-weight: 500; line-height: 21px; color: #00203f">{{ $t('views.inventory.modals.downloadLabels.title') }}</div>
                    <button type="button" class="close-button btn btn-link" @click="closeModal"><i class="bi bi-x-lg" style="color: #677788"></i></button>
                </div>
                <div class="modal-body">
                    <div v-if="errorMessage" class="alert alert-dismissible alert-soft-danger" role="alert">
                        <span v-html="errorMessage"></span>
                        <button type="button" class="btn-close" @click="errorMessage = null"></button>
                    </div>
                    <table class="table table-borderless table-thead-bordered modal-table">
                        <thead class="thead-light">
                            <tr>
                                <th>{{ $t('views.inventory.modals.downloadLabels.product.title') }}</th>
                                <th>{{ $t('views.inventory.modals.downloadLabels.quantity.title') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in products" :key="item.id">
                                <td>
                                    <div class="d-flex align-items-center" style="gap: 10px">
                                        <div class="text-center" style="width: 50px">
                                            <ImagePlaceholder :img_url="item.img_url" />
                                        </div>
                                        <div class="d-flex flex-column">
                                            <div class="text-ellipsis title">{{ item.title }}</div>
                                            <div class="d-flex" style="gap: 5px">
                                                <span>ASIN:</span>
                                                <InventoryClipboard :asin="item.asin" />
                                            </div>
                                            <div class="d-flex" style="gap: 5px">
                                                <span>SKU:</span>
                                                <InventoryClipboard type="sku" :asin="item.asin" :sku="item.sku" />
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <CurrencyInput
                                        v-model="item.quantity"
                                        :options="{
                                            valueRange: {
                                                min: 1,
                                                max: undefined,
                                            },
                                            useGrouping: false,
                                        }"
                                        :placeholder="$t('views.inventory.modals.downloadLabels.quantity.placeholder')"
                                        class="form-control custom-form-control"
                                        @keyup="
                                            (e) => {
                                                if (e.target.value == '0') {
                                                    item.quantity = null
                                                }
                                            }
                                        "
                                        @keydown.tab.prevent="focusNextInput($event)"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-primary btn-sm" :disabled="isSubmitButtonDisabled" @click="download">{{ $t('views.inventory.modals.downloadLabels.download') }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { Modal } from 'bootstrap'
import { countryInfo } from '@/utils/helper'
import InventoryClipboard from '../InventoryClipboard.vue'
import ImagePlaceholder from '../ImagePlaceholder.vue'
import CurrencyInput from '@/components/CurrencyInput.vue'

const countries = countryInfo()

export default {
    name: 'DownloadLabelsModal',
    components: { InventoryClipboard, ImagePlaceholder, CurrencyInput },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            countries,
            modalID: 'DownloadLabelsModal-' + crypto.randomUUID(),
            modalInstance: null,
            products: [],
            errorMessage: null,
        }
    },
    computed: {
        ...mapState('inventory', ['list', 'selectedRows']),
        ...mapGetters('market', ['currentMarket']),
        isSubmitButtonDisabled() {
            return this.products.some((item) => !item.quantity)
        },
        getAmazonCountryURL() {
            return countries[this.currentMarket.short].domain
        },
    },
    methods: {
        ...mapActions('inventory', ['downloadLabels']),
        closeModal() {
            this.modalInstance.hide()
            this.$emit('update:modelValue', false)
        },
        download() {
            const items = this.products.map((item) => {
                return {
                    id: item.id,
                    quantity: item.quantity,
                }
            })
            this.downloadLabels({ items })
                .then(() => {
                    this.closeModal()
                })
                .catch((e) => {
                    if (e.response?.data?.message.includes('are not valid')) {
                        this.errorMessage = e.response?.data?.message
                        return
                    }
                    this.errorMessage = this.$t('views.inventory.modals.downloadLabels.toast.error')
                })
        },
        focusNextInput(event) {
            const inputs = Array.from(document.querySelectorAll(`#${this.modalID} input`))
            const currentIndex = inputs.indexOf(event.target)
            const nextInput = inputs[currentIndex + 1]

            if (nextInput) {
                nextInput.focus()
            } else {
                inputs[0].focus()
            }
        },
    },
    watch: {
        modelValue: function (data) {
            if (data) {
                const filteredItems = this.list.data
                    .filter((item) => this.selectedRows.includes(item.id))
                    .map((item) => ({
                        id: item.id,
                        title: item.product.title,
                        asin: item.identifiers.asin,
                        sku: item.identifiers.sku,
                        img_url: item.product.img_url,
                        quantity: null,
                    }))
                this.products = filteredItems
                const modalEl = document.getElementById(this.modalID)
                this.modalInstance = new Modal(modalEl, { backdrop: true })
                this.modalInstance.show()
                modalEl.addEventListener('hidden.bs.modal', () => {
                    this.closeModal()
                })
            } else {
                this.closeModal()
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.dp__theme_light {
    --dp-primary-color: #ffb53e;
}
.custom-form-control {
    max-height: 37px;
    font-size: 12px;
}
[id^='DownloadLabelsModal-'] {
    .modal-body {
        padding-bottom: 0 !important;
    }
    .modal-footer {
        border-top: none !important;
    }
    .close-button {
        width: 35px;
        height: 35px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;

        &:hover {
            background-color: #f8fafd;
        }
    }
}
.modal-table {
    margin-bottom: 0 !important;

    tbody {
        tr {
            border-bottom: 1px solid #e7eaf3b2;

            td {
                height: 67px;
                vertical-align: middle;
            }
        }
    }

    .text-ellipsis {
        font-size: 11.9px;
        font-weight: 400;
        line-height: 17.9px;
        color: #677788;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;

        &.title {
            max-width: 250px;
        }

        &.category {
            max-width: 125px;
        }
    }
    .dp__input_wrap {
        width: auto;
        > input {
            padding-inline-end: 12px !important;
            font-size: 14px;
            border: 0.6px solid #e7eaf3;
            color: #677788;
            width: 125px !important;
        }
    }
}
</style>
